import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { AlertController, ToastController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { isEqual } from 'lodash-es';
import { Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { defaultToastDuration } from '../../../../constants/toast.constants';
import * as fromNetSheetActions from '../../../../store/actions/net-sheet.actions';
import * as fromNetSheetSelectors from '../../../../store/selectors/net-sheet.selectors';
import * as fromUserSelectors from '../../../../store/selectors/user.selectors';
import { NetSheetResultSection } from '../../../../typings/net-sheet';
import { GenericComponent } from '../../../components/generic/generic.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-profile-report',
  standalone: false,
  templateUrl: './report.page.html'
})
export class ReportPageComponent extends GenericComponent {
  private alertController = inject(AlertController);
  private store = inject(Store);
  private toastController = inject(ToastController);

  hasCalculatorReport$: Observable<boolean>;
  subtitles$: Observable<string[]>;
  sections$: Observable<NetSheetResultSection[]>;

  hasFooter$: Observable<boolean>;
  notes$: Observable<string[]>;
  disclaimers$: Observable<string[]>;
  copyright$: Observable<string>;

  userEmail: string | null = null;

  constructor() {
    super();
    this.hasCalculatorReport$ = this.store.pipe(
      select(fromNetSheetSelectors.selectHasNetSheetCalculatorResult),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.subtitles$ = this.store.pipe(
      select(fromNetSheetSelectors.selectNetSheetCalculatorReportSubtitle),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.sections$ = this.store.pipe(
      select(fromNetSheetSelectors.selectNetSheetCalculatorReportSections),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.hasFooter$ = this.store.pipe(
      select(fromNetSheetSelectors.selectHasNetSheetCalculatorReportFooter),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.notes$ = this.store.pipe(
      select(fromNetSheetSelectors.selectHasNetSheetCalculatorReportFooterNotes),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.disclaimers$ = this.store.pipe(
      select(fromNetSheetSelectors.selectHasNetSheetCalculatorReportFooterDisclaimers),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.copyright$ = this.store.pipe(
      select(fromNetSheetSelectors.selectHasNetSheetCalculatorReportFooterCopyright),
      throttleTime(200, undefined, { leading: true, trailing: true }),
      distinctUntilChanged((previous, current) => isEqual(previous, current))
    );

    this.store.pipe(select(fromUserSelectors.selectEmail)).subscribe((email) => {
      this.userEmail = email;
    });
  }

  async send() {
    const alert = await this.alertController.create({
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          handler: async (data) => {
            if (
              String(data.email)
                .toLowerCase()
                .match(
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                )
            ) {
              this.store.dispatch(
                fromNetSheetActions.emailExportPdf({
                  payload: {
                    email: data.email
                  }
                })
              );
              return true;
            } else {
              const toast = await this.toastController.create({
                duration: defaultToastDuration,
                message: 'Invalid email address',
                position: 'top'
              });
              await toast.present();
              return false;
            }
          },
          role: 'confirm',
          text: 'Send'
        }
      ],
      header: 'Email Net Sheet',
      inputs: [
        {
          label: 'Email Address',
          name: 'email',
          placeholder: 'email@example.com',
          type: 'email',
          value: this.userEmail
        }
      ],
      message: `Your net sheet will be delivered via email to the following address:`
    });
    await alert.present();
  }
}
